/**
 * Cashier Analytics — drill-down comparison of every cashier's lifetime
 * performance. Accessible from All-Time Financials → "Compare Cashiers".
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, RefreshCw, Trophy, Users, Package, Receipt, Undo2,
  Calendar, Clock, Flame, Star, Coins, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtR = (v) => "R " + (Number(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtInt = (v) => (Number(v) || 0).toLocaleString("en-ZA");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const rankMedal = (i) => {
  if (i === 0) return { icon: "🥇", tone: "text-yellow-600" };
  if (i === 1) return { icon: "🥈", tone: "text-gray-500" };
  if (i === 2) return { icon: "🥉", tone: "text-amber-700" };
  return { icon: `#${i + 1}`, tone: "text-gray-400" };
};

// Refund-rate risk colour
const refundBadge = (pct) => {
  if (pct >= 10) return "bg-rose-100 text-rose-800 border-rose-300";
  if (pct >= 5) return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-emerald-100 text-emerald-800 border-emerald-300";
};

export default function CashierAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cashiers, setCashiers] = useState([]);
  const [selectedName, setSelectedName] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/financial-summary/cashier-analytics`);
      setCashiers(res.data?.cashiers || []);
      if (res.data?.cashiers?.length && !selectedName) {
        setSelectedName(res.data.cashiers[0].name);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load cashier analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selected = useMemo(
    () => cashiers.find((c) => c.name === selectedName) || cashiers[0],
    [cashiers, selectedName]
  );

  // Turnover comparison chart
  const chartData = cashiers.slice(0, 10).map((c) => ({
    name: c.name.length > 12 ? c.name.slice(0, 10) + "…" : c.name,
    turnover: c.turnover,
    txns: c.transactions,
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 pb-10" data-testid="cashier-analytics-page">
      {/* Header */}
      <div className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/yearly-summary/all-time")} className="text-white hover:bg-white/10">
            <ArrowLeft className="h-4 w-4 mr-1" /> All-Time
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-black flex items-center gap-2">
              <Trophy className="h-6 w-6 text-amber-400" />
              Compare Cashiers
            </h1>
            <p className="text-xs text-slate-300 mt-0.5">Lifetime performance drill-down</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
            className="border-white/20 bg-white/10 text-white hover:bg-white/20">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {!cashiers.length ? (
          <Card className="p-10 text-center text-gray-500">
            <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
            No cashier data yet.
          </Card>
        ) : (
          <>
            {/* Turnover chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Coins className="h-4 w-4 text-purple-600" /> Cashier Turnover Ranking
                </CardTitle>
                <CardDescription>Every cashier by lifetime turnover</CardDescription>
              </CardHeader>
              <CardContent style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `R${(v/1000).toFixed(0)}k` : `R${v}`} />
                    <Tooltip formatter={(v, k) => k === "turnover" ? fmtR(v) : fmtInt(v)} />
                    <Legend />
                    <Bar dataKey="turnover" name="Turnover" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Cashier list + detail split */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Left: list of cashiers */}
              <Card className="lg:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="h-4 w-4" /> {cashiers.length} Cashiers
                  </CardTitle>
                  <CardDescription>Click to drill in</CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-1 max-h-[500px] overflow-y-auto">
                  {cashiers.map((c, i) => {
                    const medal = rankMedal(i);
                    const active = selectedName === c.name;
                    return (
                      <button
                        key={c.name}
                        onClick={() => setSelectedName(c.name)}
                        className={`w-full text-left rounded-lg p-2.5 transition-all border ${
                          active ? "bg-purple-100 border-purple-400 shadow-sm" : "bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                        data-testid={`cashier-row-${c.name}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`text-lg ${medal.tone}`}>{medal.icon}</span>
                            <span className="font-semibold text-sm truncate">{c.name}</span>
                          </div>
                          <span className="text-xs font-bold text-purple-700 whitespace-nowrap">
                            {fmtR(c.turnover)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-600">
                          <span>{fmtInt(c.transactions)} txns</span>
                          <span className="text-gray-400">·</span>
                          <span>avg {fmtR(c.avg_basket)}</span>
                          {c.refund_rate_pct > 0 && (
                            <>
                              <span className="text-gray-400">·</span>
                              <span className={`px-1.5 rounded ${refundBadge(c.refund_rate_pct)}`}>
                                {c.refund_rate_pct.toFixed(1)}% ref
                              </span>
                            </>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Right: selected cashier detail */}
              <div className="lg:col-span-2 space-y-4">
                {selected && (
                  <>
                    <Card className="border-2 border-purple-200 shadow-md overflow-hidden">
                      <CardHeader className="bg-gradient-to-r from-purple-100 via-fuchsia-50 to-purple-100 border-b border-purple-200 pb-3">
                        <CardTitle className="text-xl flex items-center gap-2">
                          <Star className="h-5 w-5 text-purple-600" />
                          {selected.name}
                        </CardTitle>
                        <CardDescription>Full lifetime profile</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Metric icon={Coins} label="Turnover" value={fmtR(selected.turnover)} color="purple" />
                        <Metric icon={Receipt} label="Transactions" value={fmtInt(selected.transactions)} color="blue" />
                        <Metric icon={Package} label="Avg Basket" value={fmtR(selected.avg_basket)} color="emerald" />
                        <Metric icon={Package} label="Items Sold" value={fmtInt(selected.items_sold)} color="slate" />
                        <Metric icon={Users} label="Unique Customers" value={fmtInt(selected.unique_customers)} color="amber" />
                        <Metric icon={Flame} label="Longest Streak" value={`${fmtInt(selected.longest_streak_days)} days`} color="rose" />
                        <Metric icon={Calendar} label="Best Day" value={selected.top_day || "—"} sub={fmtR(selected.top_day_turnover)} color="indigo" />
                        <Metric icon={Clock} label="Peak Hour" value={selected.top_hour != null ? `${selected.top_hour}:00` : "—"} sub={fmtR(selected.top_hour_turnover)} color="teal" />
                      </CardContent>
                    </Card>

                    {/* Refund + petty cash + timeline */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card className={`border-2 ${selected.refund_rate_pct >= 10 ? "border-rose-300" : "border-gray-200"}`}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Undo2 className="h-4 w-4 text-rose-600" /> Customer Service Signal
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-600">Refund count</span>
                            <span className="font-bold">{fmtInt(selected.refund_count)}</span>
                          </div>
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-600">Refund value</span>
                            <span className="font-bold">{fmtR(selected.refund_value)}</span>
                          </div>
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-600">Refund rate</span>
                            <Badge className={refundBadge(selected.refund_rate_pct)}>
                              {selected.refund_rate_pct.toFixed(1)}%
                            </Badge>
                          </div>
                          {selected.refund_rate_pct >= 10 && (
                            <div className="mt-2 text-xs text-rose-700 flex items-start gap-1.5 bg-rose-50 border border-rose-200 rounded p-2">
                              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                              High refund rate — may indicate ring-up errors or customer-service issues worth investigating.
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Coins className="h-4 w-4 text-amber-600" /> Till Activity
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2">
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-600">Petty cash withdrawals</span>
                            <span className="font-bold">{fmtInt(selected.petty_count)}</span>
                          </div>
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-600">Petty cash value</span>
                            <span className="font-bold">{fmtR(selected.petty_value)}</span>
                          </div>
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-600">Trading days</span>
                            <span className="font-bold">{fmtInt(selected.trading_days)}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-600" /> Career Span
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0 grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-600 uppercase font-semibold">First Sale</div>
                          <div className="text-base font-bold">{fmtDate(selected.first_sale)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600 uppercase font-semibold">Last Sale</div>
                          <div className="text-base font-bold">{fmtDate(selected.last_sale)}</div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Metric mini tile ----
const Metric = ({ icon: Icon, label, value, sub, color }) => {
  const colors = {
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
  }[color];
  return (
    <div className={`rounded-lg border p-2.5 ${colors}`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className="h-3.5 w-3.5 opacity-70" />
        <span className="text-[10px] uppercase font-bold opacity-70 tracking-wider">{label}</span>
      </div>
      <div className="text-lg font-black">{value}</div>
      {sub && <div className="text-[10px] opacity-70">{sub}</div>}
    </div>
  );
};
